import { EMustContainsChecked, EMustNotExcludesChecked, ICommonSettings, IPasswordPolicy } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigTooltip } from "../app-settings-config-tooltip";
import { ConfigModalBody, ConfigModalFooter, ConfigModalGroupHeading, ConfigModalNote } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";
import "./app-settings-config-password-policy.scss";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const PASSWORD_POLICY_SEARCH_TERMS: string[] = [
  "Must contains",
  "Minimum lowercase",
  "Minimum uppercase",
  "Minimum number",
  "Minimum special",
  "Must NOT contains",
  "Words",
  "Sequential Digits",
  "Recurring",
  "Birth Date",
  "Name or Surname",
  "Password Length",
  "Password Age",
  "Notify user before expire",
  "Account lockout",
  "Treshold",
  "Duration",
  "CAPTCHA",
  "Incorrectly times",
  "Last Passwords",
  "Not the same as last"
];

/** The strip's labels are its own locators — they are what the suite clicks. */
const PASSWORD_POLICY_TABS: string[] = ["CHARACTERS AND SIZE", "LOCKING"];

export interface IConfigPasswordPolicyState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  activeTab: string;
  editingPolicy?: IPasswordPolicy;
  mustContainsIds?: EMustContainsChecked[];
  mustNotExcludesIds?: EMustNotExcludesChecked[];
  isMinLowerCaseEnabled: boolean;
  isMinUpperCaseEnabled: boolean;
  isMinNumberEnabled: boolean;
  isMinSpecialEnabled: boolean;
  isSequentialDigitsEnabled: boolean;
  isRecurringEnabled: boolean;
  isBirthDateEnabled: boolean;
  isNameOrSurnameEnabled: boolean;
  isMinPWLengthEnabled: boolean;
  isMaxPWLengthEnabled: boolean;
}

export interface IConfigPasswordPolicyProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingPolicyId: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigPasswordPolicy extends React.PureComponent<IConfigPasswordPolicyProps, IConfigPasswordPolicyState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigPasswordPolicyProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      activeTab: PASSWORD_POLICY_TABS[0],
      editingPolicy: undefined,
      isMinLowerCaseEnabled: false,
      isMinUpperCaseEnabled: false,
      isMinNumberEnabled: false,
      isMinSpecialEnabled: false,
      isSequentialDigitsEnabled: false,
      isRecurringEnabled: false,
      isBirthDateEnabled: false,
      isNameOrSurnameEnabled: false,
      isMinPWLengthEnabled: false,
      isMaxPWLengthEnabled: false
    };
  }

  componentDidMount() {
    this.loadPolicy();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigPasswordPolicyProps) {
    if (prevProps.editingPolicyId !== this.props.editingPolicyId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadPolicy();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadPolicy();
    }
  }

  loadPolicy = () => {
    if (this.props.editingPolicyId) {
      const policy = this.props.commonSettings.passwordPolicies?.find(
        (x) => x.id === this.props.editingPolicyId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState(
        {
          editingPolicy: policy,
          isMinLowerCaseEnabled: !!policy?.minLowerCase,
          isMinUpperCaseEnabled: !!policy?.minUpperCase,
          isMinNumberEnabled: !!policy?.minDigits,
          isMinSpecialEnabled: !!policy?.minimumSpecialChar,
          isSequentialDigitsEnabled: !!policy?.sequentialDigitsCount,
          isRecurringEnabled: !!policy?.recurringCharsCount,
          isMinPWLengthEnabled: !!policy?.minPasswordLength,
          isMaxPWLengthEnabled: !!policy?.maxPasswordLength
        },
        () => {
          if (policy) {
            const fieldsValue = this.formRef.current?.getFieldsValue();
            this.formRef.current?.resetFields();

            fieldsValue.policyName = policy.name;
            fieldsValue.specialChars = policy.specialChars;
            fieldsValue.minimumSpecialChar = policy.minimumSpecialChar;
            fieldsValue.minUpperCase = policy.minUpperCase;
            fieldsValue.minLowerCase = policy.minLowerCase;
            fieldsValue.minDigits = policy.minDigits;
            fieldsValue.sequentialDigitsCount = policy.sequentialDigitsCount;
            fieldsValue.recurringCharsCount = policy.recurringCharsCount;
            fieldsValue.notContainsBirthdate = policy.notContainsBirthdate;
            fieldsValue.notContainsNameSurname = policy.notContainsNameSurname;
            fieldsValue.minPasswordLength = policy.minPasswordLength;
            fieldsValue.maxPasswordLength = policy.maxPasswordLength;
            fieldsValue.minPasswordAge = policy.minPasswordAge;
            fieldsValue.maxPasswordAge = policy.maxPasswordAge;
            fieldsValue.notifyBeforeExpired = policy.notifyBeforeExpired;
            fieldsValue.differentLastPasswordsCount = policy.differentLastPasswordsCount;
            fieldsValue.tresholdLock = policy.tresholdLock;
            fieldsValue.tresholdDuration = policy.tresholdDuration;
            fieldsValue.incorrectTimes = policy.incorrectTimes;
            fieldsValue.incorrectEntriesForEnable = policy.incorrectEntriesForEnable;
            fieldsValue.mustNotContainsWords = policy.mustNotContainsWords;
            this.formRef.current?.setFieldsValue(fieldsValue);
          }
        }
      );
    } else {
      this.setState(
        {
          isMinLowerCaseEnabled: true,
          isMinNumberEnabled: true,
          isSequentialDigitsEnabled: true,
          isRecurringEnabled: true,
          isMinUpperCaseEnabled: false,
          isMinSpecialEnabled: false,
          isBirthDateEnabled: false,
          isNameOrSurnameEnabled: false,
          isMinPWLengthEnabled: false,
          isMaxPWLengthEnabled: false
        },
        () => {
          this.formRef.current?.resetFields();
        }
      );
    }
  };

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.passwordPolicies;

    const isSettingAlreadyExist =
      settings?.some(
        (x) => x.name === value && x.id !== this.props.editingPolicyId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Password Policy Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    const obj: IPasswordPolicy = {
      id:
        this.props.editingPolicyId && this.props.editingPolicyId !== ""
          ? this.props.editingPolicyId
          : Guid.create().toString(),
      name: data.policyName,
      mustNotContainsWords: data.mustNotContainsWords,
      specialChars: data.specialChars ?? "(!@#$%^&*()_+|~-=`{}[]:`;`'<>?,./)]",
      minimumSpecialChar: parseInt(data.minimumSpecialChar),
      minUpperCase: parseInt(data.minUpperCase),
      minLowerCase: parseInt(data.minLowerCase),
      minDigits: parseInt(data.minDigits),
      sequentialDigitsCount: parseInt(data.sequentialDigitsCount),
      recurringCharsCount: parseInt(data.recurringCharsCount),
      notContainsBirthdate: data.notContainsBirthdate,
      notContainsNameSurname: data.notContainsNameSurname,
      minPasswordLength: parseInt(data.minPasswordLength),
      maxPasswordLength: parseInt(data.maxPasswordLength),
      minPasswordAge: parseInt(data.minPasswordAge),
      maxPasswordAge: parseInt(data.maxPasswordAge),
      differentLastPasswordsCount: parseInt(data.differentLastPasswordsCount),
      notifyBeforeExpired: parseInt(data.notifyBeforeExpired),
      tresholdLock: parseInt(data.tresholdLock),
      tresholdDuration: parseInt(data.tresholdDuration),
      incorrectTimes: parseInt(data.incorrectTimes),
      incorrectEntriesForEnable: parseInt(data.incorrectEntriesForEnable)
    };
    if (this.props.editingPolicyId) {
      for (const key in obj) {
        if (!obj[key]) {
          delete obj[key];
        }
      }
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "passwordPolicies",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      for (const key in obj) {
        if (!obj[key]) {
          delete obj[key];
        }
      }
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "passwordPolicies",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deletePolicy = () => {
    if (!this.props.editingPolicyId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingPolicyId || "",
      configKey: "passwordPolicies",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Password Policy setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    const fieldsValue = this.formRef?.current?.getFieldsValue();

    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Password policy setting"
        secondHeader="Password policy setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingPolicyId ? this.deletePolicy : undefined}
            deleteTestId="passwordPolicySettingDeleteButton"
            submitTestId="passwordPolicySettingCreateButton"
            submitLabel={this.props.editingPolicyId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configPasswordPolicyForm"
          />
        }
      >
        <Form
          id="configPasswordPolicyForm"
          name="passwords"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{
            overflowY: "auto",
            height: "100%",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column"
          }}
        >
          <ConfigModalBody className="kuika-config-modal__body--flush">
            <div className="kuika-password-policy">
              <div className="kuika-password-policy__group">
                <ConfigModalNote>
                  {this.props.getSectionDescription("passwordpolicysetting_passwordpolicysetting_description")}
                </ConfigModalNote>
                <ConfigField label="Name" required tooltipContentKey="passwordpolicysetting_passwordpolicysetting_name">
                  <Form.Item
                    name="policyName"
                    rules={[
                      { required: true, message: "Please enter a Password Policy Setting Name." },
                      { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Password Policy Setting Name is invalid" },
                      { validator: this.existingNameValidator }
                    ]}
                  >
                    <KuikaInput size="md" data-testid="passwordPolicySettingNameInput" autoFocus />
                  </Form.Item>
                </ConfigField>
              </div>
              <KuikaTabs
                className="kuika-password-policy__tabs"
                options={PASSWORD_POLICY_TABS}
                value={this.state.activeTab}
                onChange={(activeTab: string): void => this.setState({ activeTab })}
              />
              {/* Both panes stay mounted (`keepMounted`) — antd validates and submits only the
                  fields that are mounted, and the dialog submits the whole form whichever pane is
                  showing. */}
              <KuikaTabContent activeKey={this.state.activeTab} order={PASSWORD_POLICY_TABS} keepMounted>
                <React.Fragment key={PASSWORD_POLICY_TABS[0]}>
                  <div className="kuika-password-policy__group">
                    <ConfigModalNote>
                      {this.props.getSectionDescription(
                        "passwordpolicysetting_passwordpolicysetting_charectersandsize_description"
                      )}
                    </ConfigModalNote>
                    <ConfigModalGroupHeading>Must contains</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__rows">
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Minimum lowercase
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_minimumlowercase" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item>
                            <KuikaSwitch
                              checked={this.state.isMinLowerCaseEnabled}
                              onChange={(isMinLowerCaseEnabled) => {
                                this.setState({ isMinLowerCaseEnabled });
                              }}
                            />
                          </Form.Item>
                          {this.state.isMinLowerCaseEnabled && (
                            <Form.Item name="minLowerCase">
                              <KuikaInput size="md" type="number" />
                            </Form.Item>
                          )}
                        </div>
                      </div>

                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Minimum uppercase
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_minimumuppercase" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item>
                            <KuikaSwitch
                              checked={this.state.isMinUpperCaseEnabled}
                              onChange={(isMinUpperCaseEnabled) => {
                                this.setState({ isMinUpperCaseEnabled });
                              }}
                            />
                          </Form.Item>
                          {this.state.isMinUpperCaseEnabled && (
                            <Form.Item name="minUpperCase">
                              <KuikaInput size="md" type="number" />
                            </Form.Item>
                          )}
                        </div>
                      </div>

                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Minimum number
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_minimumnumber" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item>
                            <KuikaSwitch
                              checked={this.state.isMinNumberEnabled}
                              onChange={(isMinNumberEnabled) => {
                                this.setState({ isMinNumberEnabled });
                              }}
                            />
                          </Form.Item>
                          {this.state.isMinNumberEnabled && (
                            <Form.Item name="minDigits">
                              <KuikaInput size="md" type="number" />
                            </Form.Item>
                          )}
                        </div>
                      </div>

                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Minimum special
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_minimumspecial" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item>
                            <KuikaSwitch
                              checked={this.state.isMinSpecialEnabled}
                              onChange={(isMinSpecialEnabled) => {
                                this.setState({ isMinSpecialEnabled });
                              }}
                            />
                          </Form.Item>
                          {this.state.isMinSpecialEnabled && (
                            <>
                              <Form.Item name="minimumSpecialChar">
                                <KuikaInput size="md" type="number" />
                              </Form.Item>
                              <Form.Item name="specialChars" className="kuika-password-policy__grow">
                                <KuikaInput size="md" defaultValue="(!@#$%^&*()_+|~-=\`{}[]:`;`'<>?,./)]" />
                              </Form.Item>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="kuika-password-policy__group">
                    <ConfigModalGroupHeading>Must NOT contains</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__row kuika-password-policy__row--top">
                      <span className="kuika-password-policy__label">
                        Words
                        <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_words" />
                      </span>
                      <div className="kuika-password-policy__control kuika-password-policy__control--wide">
                        <Form.Item name="mustNotContainsWords">
                          <KuikaSelectbox
                            size="md"
                            mode="tags"
                            tokenSeparators={[","]}
                            placeholder="Type a word and press Enter"
                            className="kuika-password-policy__words"
                            // A free-text list, not a chooser: there are no options to
                            // open, which is what the old `dropdownStyle: none` was for.
                            visible={false}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div>
                      <div className="kuika-password-policy__rows">
                        <div className="kuika-password-policy__row">
                          <span className="kuika-password-policy__label">
                            Sequential Digits
                            <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_sequentialdigits" />
                          </span>
                          <div className="kuika-password-policy__control">
                            <Form.Item>
                              <KuikaSwitch
                                checked={this.state.isSequentialDigitsEnabled}
                                onChange={(isSequentialDigitsEnabled) => {
                                  this.setState({ isSequentialDigitsEnabled });
                                }}
                              />
                            </Form.Item>
                            {this.state.isSequentialDigitsEnabled && (
                              <Form.Item name="sequentialDigitsCount">
                                <KuikaInput size="md" type="number" />
                              </Form.Item>
                            )}
                          </div>
                        </div>

                        <div className="kuika-password-policy__row">
                          <span className="kuika-password-policy__label">
                            Recurring
                            <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_recuring" />
                          </span>
                          <div className="kuika-password-policy__control">
                            <Form.Item>
                              <KuikaSwitch
                                checked={this.state.isRecurringEnabled}
                                onChange={(isRecurringEnabled) => {
                                  this.setState({ isRecurringEnabled });
                                }}
                              />
                            </Form.Item>
                            {this.state.isRecurringEnabled && (
                              <>
                                <Form.Item name="recurringCharsCount">
                                  <KuikaInput size="md" type="number" />
                                </Form.Item>
                                <span className="kuika-password-policy__unit">char(s)</span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="kuika-password-policy__row">
                          <span className="kuika-password-policy__label">
                            Birth Date
                            <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_birthdate" />
                          </span>
                          <div className="kuika-password-policy__control">
                            <Form.Item name="notContainsBirthdate" valuePropName="checked">
                              <KuikaSwitch />
                            </Form.Item>
                          </div>
                        </div>

                        <div className="kuika-password-policy__row">
                          <span className="kuika-password-policy__label">
                            Name or Surname
                            <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_name_surname" />
                          </span>
                          <div className="kuika-password-policy__control">
                            <Form.Item name="notContainsNameSurname" valuePropName="checked">
                              <KuikaSwitch />
                            </Form.Item>
                          </div>
                        </div>
                      </div>
                    </div>
                    <ConfigModalGroupHeading>Password Length</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__rows">
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Minimum
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_minimum" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item>
                            <KuikaSwitch
                              checked={this.state.isMinPWLengthEnabled}
                              onChange={(isMinPWLengthEnabled) => {
                                this.setState({ isMinPWLengthEnabled });
                              }}
                            />
                          </Form.Item>
                          {this.state.isMinPWLengthEnabled && (
                            <>
                              <Form.Item name="minPasswordLength">
                                <KuikaInput size="md" type="number" />
                              </Form.Item>
                              <span className="kuika-password-policy__unit">char(s)</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Maximum
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_charectersandsize_maximum" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item>
                            <KuikaSwitch
                              checked={this.state.isMaxPWLengthEnabled}
                              onChange={(isMaxPWLengthEnabled) => {
                                this.setState({ isMaxPWLengthEnabled });
                              }}
                            />
                          </Form.Item>
                          {this.state.isMaxPWLengthEnabled && (
                            <>
                              <Form.Item name="maxPasswordLength">
                                <KuikaInput size="md" type="number" />
                              </Form.Item>
                              <span className="kuika-password-policy__unit">char(s)</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
                <React.Fragment key={PASSWORD_POLICY_TABS[1]}>
                  <div className="kuika-password-policy__group">
                    <ConfigModalNote>
                      {this.props.getSectionDescription(
                        "passwordpolicysetting_passwordpolicysetting_locking_description"
                      )}
                    </ConfigModalNote>
                    <ConfigModalGroupHeading>Password Age</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__rows">
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Minimum
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_passwordage_minimum" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="minPasswordAge">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                          <span className="kuika-password-policy__unit">day(s)</span>
                        </div>
                      </div>
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Maximum
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_passwordage_maximum" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="maxPasswordAge">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                          <span className="kuika-password-policy__unit">day(s)</span>
                        </div>
                      </div>
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Notify user before expire
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_passwordage_notifyuserbeforeexpire" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="notifyBeforeExpired">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                          <span className="kuika-password-policy__unit">day(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="kuika-password-policy__group">
                    <ConfigModalGroupHeading>Account lockout</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__rows">
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Treshold
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_acoountlockout_treshold" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="tresholdLock">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                          <span className="kuika-password-policy__unit">try count</span>
                        </div>
                      </div>
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Duration
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_acoountlockout_duration" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="tresholdDuration">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                          <span className="kuika-password-policy__unit">minute(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="kuika-password-policy__group">
                    <ConfigModalGroupHeading>CAPTCHA</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__rows">
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Incorrectly times
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_captcha_incorrectlytimes" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="incorrectTimes">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                        </div>
                      </div>
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Incorrectly enteres for enable
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_captcha_incorrectlyenteresforenable" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="incorrectEntriesForEnable">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="kuika-password-policy__group">
                    <ConfigModalGroupHeading>Last Passwords</ConfigModalGroupHeading>
                    <div className="kuika-password-policy__rows">
                      <div className="kuika-password-policy__row">
                        <span className="kuika-password-policy__label">
                          Not the same as last
                          <ConfigTooltip contentKey="passwordpolicysetting_passwordpolicysetting_locking_lastpasswords_notthesameaslast" />
                        </span>
                        <div className="kuika-password-policy__control">
                          <Form.Item name="differentLastPasswordsCount">
                            <KuikaInput size="md" type="number" />
                          </Form.Item>
                          <span className="kuika-password-policy__unit">password(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              </KuikaTabContent>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
